import { useEffect, useState } from "react";
import { ShieldCheck, Database, Mail, Activity as Pulse, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

function StatCard({ label, value, accent = "text-[#1E3A8A]", testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1.5 text-3xl font-display font-bold ${accent}`}>{value ?? "—"}</p>
    </div>
  );
}

function StatusPill({ ok, label, testid }) {
  const Icon = ok ? CheckCircle2 : AlertCircle;
  const cls = ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200";
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${cls}`} data-testid={testid}>
      <Icon className="w-4 h-4" />
      <span className="text-sm font-semibold">{label}</span>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function DevDashboard() {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [h, m] = await Promise.all([
        api.get("/admin/dev/health"),
        api.get("/admin/dev/metrics"),
      ]);
      setHealth(h.data);
      setMetrics(m.data);
    } catch (e) {
      // surface error in UI rather than throw
    }
    setLoading(false);
    setRefreshing(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-[#475569]" data-testid="dev-dashboard-loading">Loading…</div>;

  return (
    <div data-testid="dev-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#1E3A8A]" /> Developer Dashboard
          </h2>
          <p className="text-sm text-[#475569] mt-1">Business metrics &amp; system health — no customer PII.</p>
        </div>
        <div className="flex items-center gap-2">
          {health?.resend_configured && health?.backend_status === "online" && (
            <span
              data-testid="production-ready-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tracking-wide"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Production Ready
            </span>
          )}
          <button onClick={load} data-testid="dev-refresh" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Health */}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">System status</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatusPill ok={health?.backend_status === "online"} label={`Backend ${health?.backend_status || "?"}`} testid="dev-status-backend" />
        <StatusPill ok={health?.database_status === "online"} label={`Database ${health?.database_status || "?"}`} testid="dev-status-db" />
        <StatusPill ok={!!health?.resend_configured} label={`Resend ${health?.resend_status || "?"}`} testid="dev-status-resend" />
        <StatusPill ok={true} label="Website online" testid="dev-status-website" />
      </div>

      {/* Counts */}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Estimate requests</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        <StatCard label="Total requests" value={metrics?.total_estimate_requests} testid="dev-stat-total" />
        <StatCard label="Today" value={metrics?.today} accent="text-[#10B981]" testid="dev-stat-today" />
        <StatCard label="This week" value={metrics?.this_week} accent="text-[#7C3AED]" testid="dev-stat-week" />
        <StatCard label="This month" value={metrics?.this_month} accent="text-[#0EA5E9]" testid="dev-stat-month" />
        <StatCard label="Avg per month" value={metrics?.average_per_month} accent="text-[#F59E0B]" testid="dev-stat-avg" />
      </div>

      {/* Email delivery */}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Email delivery</h3>
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft" data-testid="dev-email-success">
          <Mail className="w-5 h-5 text-emerald-600" />
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Notifications sent</p>
          <p className="text-2xl font-display font-bold text-[#0F172A]">{metrics?.email?.notifications_sent ?? 0}</p>
          <p className="text-xs text-slate-500 mt-1">Customer confirmations: {metrics?.email?.customer_confirmations_sent ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft" data-testid="dev-email-failed">
          <Pulse className="w-5 h-5 text-amber-600" />
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Notifications failed</p>
          <p className="text-2xl font-display font-bold text-[#0F172A]">{metrics?.email?.notifications_failed ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft" data-testid="dev-email-meta">
          <Database className="w-5 h-5 text-[#1E3A8A]" />
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Last successful</p>
          <p className="text-sm text-[#0F172A] mt-0.5">{fmt(metrics?.email?.last_successful_email_at)}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Last failed</p>
          {metrics?.email?.last_failed_email_at ? (
            <>
              <p className="text-sm text-[#0F172A] mt-0.5">{fmt(metrics.email.last_failed_email_at)}</p>
              {metrics.email.last_failed_email_error && (
                <p className="text-xs text-amber-700 mt-1 break-words">{metrics.email.last_failed_email_error}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400 italic mt-0.5">None</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 text-xs text-[#475569]" data-testid="dev-config-meta">
        <p><span className="font-semibold text-[#0F172A]">Sender:</span> {health?.sender_name} &lt;{health?.sender_email}&gt;</p>
        <p className="mt-1"><span className="font-semibold text-[#0F172A]">Reply-to:</span> {health?.reply_to || "—"}</p>
        <p className="mt-1"><span className="font-semibold text-[#0F172A]">Notifications go to:</span> {health?.notification_email}</p>
        <p className="mt-3 text-slate-400">Customer data (names, phones, emails, addresses, notes) is intentionally not shown on this page.</p>
      </div>
    </div>
  );
}
